/**
 * The officials roster — module 05, `04-officials-profiles.md` **Part A**.
 *
 * `/officials`. Part B, the individual profile at `/officials/:officialId`, was built first
 * so that a roster row would have somewhere real to open; this is the page that does the
 * opening. Module 05 is whole with it.
 *
 * **§A1's layout, in order and complete:** the `Technical Panel` roster band · the
 * `Technical Officials` panel · the `TOs Expectation Summary` · the `Umpiring Panel` band ·
 * the `Umpiring Officials` panel · the `UMs Expectation Summary`.
 *
 * **One read, and it is one the API already serves: `GET /reports/progress`.** §A3's eight
 * columns are `Official Role` · *(avatar)* · `Full Name` · `Country` · `Cap` ·
 * `Report Status` · `Email Address` · `Mobile Number`, and that projection carries all eight
 * for every listed official. `GET /officials`, the obvious-looking choice, carries no email
 * address, no mobile number and no report stage — three of the eight — so the roster would
 * have needed a second call per person or a new route. Nothing was added to the API for this
 * page until its `FIH Grading` column, and that was added to the same body rather than read
 * from a second route — see below.
 *
 * **The consequence, and it is the point rather than a side effect.** The Reporting page's
 * two panels read the same body from the same route, so the `Report Status` pill and the
 * `Owned By UM` marker are literally the same derived values on both screens — which is what
 * §A3 demands of that map: *"the mockups duplicate this map verbatim between the roster and
 * the Reporting page. In the build it must be one shared derived value, not two copies."*
 * The panels themselves are one component (`features/reporting/RosterPanel`), for the same
 * reason.
 *
 * **Who is listed (rulings R2′ and R1′).** The server excludes the Technical Delegate and the
 * Tournament Administrator from that projection, which is exactly what §A2's own count line
 * asks for — `22 officials · 2 ATDs · 6 TOs · 14 Judges` counts no TD — and R2′ requires: the
 * TD receives no report and no marks, so he carries no pill and is in no denominator. One row
 * per official for the whole tournament, whichever competitions they worked (R1′).
 *
 * **Ruling R-4′.** This page reads no note route of any kind. Nothing about a UM's unreleased
 * notes reaches it — no count, no timestamp, no content and not the existence badge either.
 * The one body it fetches carries no note-derived field at all.
 *
 * **The kicker is the module's own.** §A2's line — `The Tournament Roster · Technical Panel
 * And Umpiring Panel` — is already `modules.ts`'s entry for module 05, because the roster is
 * module 05's landing page. Claiming it again through `usePageKicker` would be a second copy
 * of one string that could drift from the first; the profile page claims its line because
 * §B composes it from the official it fetched, and there is nothing to compose here.
 *
 * **What is NOT here, and why (§A5).** No sort control, no pagination, no bulk actions and no
 * export — §A5 records all four as deliberate, filtering is by role tab only and sort is
 * fixed source order. A **search field is here**, which §A5's mockup did not have: CC-Q11 was
 * ruled on 2026-08-11 (accepted) and its recommendation — *"if time is short, ship search on
 * the roster page only"* — is now a ruling, and forty-eight people is past the point where
 * scanning is the only way to find one.
 *
 * **RUN 31 — `avatarSize="xl"`, on both panels and on this page only.** The Technical Delegate
 * asked, while testing, for the profile pictures on this listing to be twice the size and for
 * the Country cell — the flag and its three-letter code — to be about 80% bigger. Both panels
 * here are the same `RosterPanel` the Reporting page draws its §A4 pair with, so the size is
 * asked for by the page rather than baked into the component: he was reading THIS listing, and
 * a screen he did not complain about should not move because a screen he did complain about
 * did. See `RosterPanelProps.avatarSize`.
 *
 * **`FIH Grading`, on both panels and on this page only.** The Technical Delegate asked to see
 * each official's current FIH grading on the roster, and it is asked for here — `showGrading`
 * — on the same terms as the `xl` pictures: this is the listing he reads the panel from, and
 * the Reporting page's pair are not. The grading rides on the same `GET /reports/progress`
 * body as every other column, so the page still makes its one read. It is the grading for the
 * role held at this tournament, and an official with none for that role reads `—`.
 */

import { useState } from "react";

import type { OfficialRole } from "@hockeytd/shared";

import { fetchReportProgress } from "../api/client.js";
import type { ReportProgressBody } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import { ExpectationSummaryPanel } from "../features/officials/ExpectationSummaryPanel.js";
import { RosterBand } from "../features/officials/RosterBand.js";
import { ROSTER_PANEL_NOTE, RosterPanel } from "../features/reporting/RosterPanel.js";

/** §A2's technical band, and §A3's technical role tabs, in the order the spec lists them. */
const TECHNICAL_ROLES: readonly OfficialRole[] = ["ATD", "TO", "Judge"];
const UMPIRING_ROLES: readonly OfficialRole[] = ["UM", "Umpire"];

export function OfficialsRosterPage(): React.JSX.Element {
  const state = useAsync<ReportProgressBody>(fetchReportProgress, []);
  const [technicalRole, setTechnicalRole] = useState<OfficialRole | null>(null);
  const [umpiringRole, setUmpiringRole] = useState<OfficialRole | null>(null);
  const [query, setQuery] = useState("");

  const officials = state.status === "ready" ? state.data.officials : [];
  const technical = officials.filter((official) => TECHNICAL_ROLES.includes(official.role));
  const umpiring = officials.filter((official) => UMPIRING_ROLES.includes(official.role));

  return (
    <>
      <div className="repband">
        <h2>
          The Tournament Roster{" "}
          {state.status === "ready" ? (
            <span className="md">· {state.data.officials.length} Officials Listed</span>
          ) : null}
        </h2>
        <HowToUse id="officials-roster" title="How to use The Tournament Roster">
          <li>
            This is every official on the technical and umpiring panels, exactly as TMS holds
            them.
          </li>
          <li>
            Type into <HowToLink to="roster-search">Search The Roster</HowToLink> to narrow both
            panels at once — by name, country, role or email address.
          </li>
          <li>
            Searching narrows the lists; it never reorders them and never hides a panel, so{" "}
            <HowToLink to="tech-band">Technical Panel</HowToLink> and{" "}
            <HowToLink to="ump-band">Umpiring Panel</HowToLink> always read in the same
            order.
          </li>
          <li>Click any name to open that person&rsquo;s profile.</li>
        </HowToUse>
        <span style={{ flex: 1 }} />
      </div>

      {/*
       * CC-Q11's roster search. One field over both panels rather than one per panel: the TD
       * looking for a name does not know, and should not have to decide, which panel that
       * person is on. It narrows; it never reorders and never hides a panel — §A5 keeps the
       * sort fixed, and a panel that vanished would read as a roster that had lost people.
       */}
      <div className="rostersearch" data-howto="roster-search">
        <label className="rostersearch__label" htmlFor="rosterSearch">
          Search The Roster
        </label>
        <input
          id="rosterSearch"
          className="rostersearch__input"
          type="search"
          value={query}
          autoComplete="off"
          placeholder="Name, Country, Role Or Email Address"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        {query.trim() === "" ? (
          <span className="rostersearch__note">Both Panels · Name, Country, Role Or Email</span>
        ) : (
          <button
            type="button"
            className="gbtn rostersearch__clear"
            onClick={() => {
              setQuery("");
            }}
          >
            Clear Search
          </button>
        )}
      </div>

      {state.status === "loading" ? <Skeleton rows={5} /> : null}
      {state.status === "error" ? <FailBanner error={state.error} /> : null}

      {state.status === "ready" ? (
        <>
          <RosterBand
            id="tech"
            title="Technical Panel"
            subtitle="ATDs, Technical Officers And Judges"
            officials={technical}
            roles={TECHNICAL_ROLES}
          />

          <RosterPanel
            id="tech"
            title="Technical Officials"
            note={ROSTER_PANEL_NOTE}
            tabsAriaLabel="Technical role filter"
            roles={TECHNICAL_ROLES}
            officials={technical}
            selectedRole={technicalRole}
            onSelectRole={setTechnicalRole}
            query={query}
            avatarSize="xl"
            showGrading
          >
            <p>
              <b>Cap counts come from TMS and the unit differs by role —</b> for{" "}
              <b>Technical Officers and Judges</b> it is the number of matches officiated at
              the technical table; for <b>Umpires</b> it is the number of matches umpired; and
              for <b>Umpires Managers</b> it is the number of <b>tournaments</b> served as UM,
              not matches. Hover any Cap figure to see its unit.
            </p>
            <p>Photos From Officials Profiles — Initials Where The Intake Has Not Completed</p>
          </RosterPanel>

          <ExpectationSummaryPanel
            id="to"
            title="TOs Expectation Summary"
            note="Common Threads Across The Technical Panel"
            threadsKicker="Most-Requested Guidance Topics"
            themeHeadings={[
              "First Time At This Level",
              "Language Support Self-Flagged",
              "Mentorship And Feedback Requested",
              "Standards Point Raised",
            ]}
            foot={
              <p>
                <i>
                  Composed from the officials&rsquo; intake replies — items 7 (Past Hockey
                  Experience) and 8 (Expectations Of The Tournament And The TD). In production
                  this digest is AI-summarised from the recorded intake answers and refreshes
                  as new submissions arrive, so the threads and the counts move as the panel
                  replies. Individual answers stay on each official&rsquo;s profile.
                </i>
              </p>
            }
          />

          <RosterBand
            id="ump"
            title="Umpiring Panel"
            subtitle="Umpires Managers And Umpires"
            officials={umpiring}
            roles={UMPIRING_ROLES}
          />

          <RosterPanel
            id="ump"
            title="Umpiring Officials"
            note={ROSTER_PANEL_NOTE}
            tabsAriaLabel="Umpiring role filter"
            roles={UMPIRING_ROLES}
            officials={umpiring}
            selectedRole={umpiringRole}
            onSelectRole={setUmpiringRole}
            query={query}
            avatarSize="xl"
            showGrading
          >
            <p>
              <b>Umpires Managers are counted in tournaments —</b> a UM cap of <b>18</b> means
              eighteen tournaments served as Umpires Manager, which is why the figures look
              small beside an umpire&rsquo;s match count.
            </p>
            <p>Photos From Officials Profiles — Initials Where The Intake Has Not Completed</p>
          </RosterPanel>

          <ExpectationSummaryPanel
            id="um"
            title="UMs Expectation Summary"
            note="How The Umpires Managers Propose To Work With The Umpire Team"
            threadsKicker="How The UM Team Wants To Work With The Umpires"
            themeHeadings={[
              "Standards Point Raised",
              "Educator Experience Within The Group",
              "Umpires The Group Wants To Develop",
              "Questions And Assistance Raised",
            ]}
            foot={
              <p>
                <i>
                  Composed from the Umpires Managers&rsquo; intake replies — items 7, 8 and 9.
                  The umpiring announcement used the nine-question variant, so this digest
                  leans towards team-working rather than personal development.
                </i>
              </p>
            }
          />
        </>
      ) : null}
    </>
  );
}
